/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

const BlogHeading = {
  parts: [
    'container',
    'articleContainer',
    'titleContainer',
    'title',
    'metadataShareContainer',
    'metadataContainer',
    'metadataRow',
    'metadataItem',
    'metadataLabel',
    'metadataValue',
    'metadataValueOnly',
    'metadataDivider',
    'shareContainer',
    'shareLabel',
    'shareDivider',
    'socialIcons',
    'socialIcon',
    'bottomDivider',
    'descriptionContainer',
    'description'
  ],

  baseStyle: () => ({
    container: {
      maxWidth: '1440px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: {base: '12', lg: '20'}, // Mobile: 48px, Desktop: 80px
      alignSelf: 'stretch',
      position: 'relative',
      paddingX: {base: 5, lg: 20}, // Mobile: 24px, Desktop: 80px
      paddingTop: {base: 4, lg: 12}, // 48px
      paddingBottom: {base: 5, lg: 20}, // 80px
      mx: 'auto',
    },
    articleContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: {base: '4', lg: '6'}, // Mobile: 16px, Desktop: 24px
      alignSelf: 'stretch',
      position: 'relative',
    },

    titleContainer: {
      display: 'flex',
      maxWidth: {base: '100%', lg: '840px'},
      justifyContent: {base: 'flex-start', lg: 'center'},
      alignItems: 'center',
      position: 'relative',
    },

    title: {
      flex: '1 0 0',
      color: 'brandColors.greyscale90',
      fontFamily: 'aktiv-grotesk',
      fontSize: {base: '32px', lg: '6xl'}, // Mobile: 32px, Desktop: 60px
      fontWeight: '400',
      lineHeight: '120%',
      letterSpacing: {base: '-0.32px', lg: '-0.6px'}, // Mobile: -0.32px, Desktop: -0.6px
      position: 'relative',
    },

    metadataShareContainer: {
      display: 'flex',
      alignItems: {base: 'flex-start', lg: 'center'},
      gap: 6,
      alignSelf: 'stretch',
      position: 'relative',
      flexDirection: {base: 'column', lg: 'row'},
    },

    metadataContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4', // 16px
      flex: '1 0 0',
      position: 'relative',
    },

    metadataRow: {
      display: 'flex',
      height: 'auto', // Allow auto height for mobile
      alignItems: 'center',
      gap: {base: '6', lg: '4'}, // Mobile: 24px, Desktop: 16px
      position: 'relative',
      flexWrap: 'wrap',
    },

    metadataItem: {
      display: 'flex',
      flexDirection: {base: 'column', lg: 'row'},
      alignItems: {base: 'flex-start', lg: 'center'},
      gap: '2', // 8px
      position: 'relative',
    },

    metadataLabel: {
      color: 'brandColors.primaryDarkLinen',
      fontFamily: 'aktiv-grotesk',
      fontSize: '2xs', // 10px
      fontWeight: '600',
      lineHeight: '120%',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      position: 'relative',
    },

    metadataValue: {
      color: 'brandColors.greyscale70',
      fontFamily: 'aktiv-grotesk',
      fontSize: 'xs', // 12px
      fontWeight: '600',
      lineHeight: '120%',
      letterSpacing: '2.4px',
      textTransform: 'uppercase',
      position: 'relative',
    },

    metadataValueOnly: {
      color: 'brandColors.primaryDarkLinen',
      fontFamily: 'aktiv-grotesk',
      fontSize: 'xs', // 12px
      fontWeight: '600',
      lineHeight: '120%',
      letterSpacing: '2.4px',
      textTransform: 'uppercase',
      position: 'relative',
    },

    metadataDivider: {
      width: 0.25, // 1px
      height: {base: 8.5, lg: 3.5},
      backgroundColor: 'brandColors.linen03',
      orientation: 'vertical',
    },

    shareContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 6, // 24px
      py: 4, // 16px
      px: 0, // 16px 0
      borderRadius: 'lg',
      position: 'relative',
    },

    shareLabel: {
      color: 'brandColors.greyscale90',
      fontFamily: 'aktiv-grotesk',
      fontSize: 'sm', // 14px
      fontWeight: '500',
      lineHeight: '120%',
      position: 'relative',
    },

    shareDivider: {
      width: 0.25, // 1px
      height: 6, // 24px
      alignSelf: 'stretch',
      backgroundColor: 'brandColors.linen03',
      orientation: 'vertical',
    },

    socialIcons: {
      display: 'flex',
      alignItems: 'center',
      gap: 6, // Icons are touching in design
      position: 'relative',
    },

    socialIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 6, // 24px
      height: 6, // 24px
      position: 'relative',
      cursor: 'pointer',
      color: 'brandColors.greyscale90',
      _hover: {
        opacity: 0.7,
      },
      _focus: {
        outline: '2px solid',
        outlineColor: 'brandColors.primaryDarkLinen',
        outlineOffset: '2px',
      },
    },

    bottomDivider: {
      width: '100%',
      height: 0,
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      position: 'relative',
    },

    descriptionContainer: {
      display: 'flex',
      justifyContent: {base: 'flex-start', lg: 'flex-end'},
      alignItems: 'center',
      gap: 2.5, // 10px
      alignSelf: 'stretch',
      position: 'relative',
    },

    description: {
      maxWidth: {base: '100%', lg: '619px'},
      color: 'brandColors.greyscale90',
      fontSize: {base: 'md', lg: 'xl'}, // Mobile: 16px, Desktop: 20px
      fontWeight: 'normal',
      lineHeight: {base: '160%', lg: '150%'}, // Mobile: 160%, Desktop: 150%
      position: 'relative',
      pt: {base: 8, lg: 0},
    },
  }),
};

export default BlogHeading;
